// ColorCycleManager - handles day/night cycle and color transitions
import { COLOR_CYCLE } from '../config/constants.js';
import { getPhaseName } from '../world/ColorPalettes.js';

export class ColorCycleManager {
    constructor(config = {}) {
        // Dependencies (set via setDependencies)
        this.gameState = null;
        this.biomeManager = null;
        this.cloudManager = null;
        this.lightingManager = null;
        this.skyManager = null;
        this.sunManager = null;
        this.scene = null;

        // Color palettes
        this.colorPalettes = config.colorPalettes || [];
        this.enableColorCycle = config.enableColorCycle !== undefined ? config.enableColorCycle : COLOR_CYCLE.ENABLED;

        // Current palette state
        this.currentPaletteIndex = config.startingPaletteIndex || 0;

        // Interpolated color values (exposed for external systems)
        this.skyColorTop = 0x87ceeb;
        this.skyColorBottom = 0xffffff;
        this.fogColor = 0xcccccc;
        this.ambientLightColor = 0xffffff;
        this.ambientLightIntensity = 0.6;
        this.directionalLightColor = 0xffffff;
        this.directionalLightIntensity = 0.8;
        this.sunColor = 0xffff00;
        this.terrainColor = 0x2d5a1f;
        this.interpolatedCloudOpacity = 0.8;
        this.vegetationEmissiveIntensity = 0.3;

        // Color transition state
        this.currentTerrainColor = null;
        this.nextTerrainColor = null;
        this.currentCloudColor = null;
        this.nextCloudColor = null;
        this.currentRockColor = null;
        this.nextRockColor = null;
        this.currentShrubColor = null;
        this.nextShrubColor = null;
        this.currentCloudOpacity = null;
        this.nextCloudOpacity = null;

        // Sub-cycle state (color variations within a phase)
        this.subCycleDuration = COLOR_CYCLE.SUB_CYCLE.DURATION;
        this.lastSubCycleTime = 0;
        this.subCycleTransitionDuration = COLOR_CYCLE.SUB_CYCLE.TRANSITION_DURATION;
        this.subCycleTransitionProgress = 0;
        this.isSubCycleTransitioning = false;

        // Biome color transition state (gradual color blending when switching biomes)
        this.isBiomeColorTransitioning = false;
        this.biomeColorTransitionProgress = 0;
        this.biomeColorTransitionDuration = COLOR_CYCLE.BIOME_TRANSITION?.DURATION || 1800; // game seconds
    }

    /**
     * Set dependencies after construction
     */
    setDependencies(deps) {
        this.gameState = deps.gameState || this.gameState;
        this.biomeManager = deps.biomeManager || this.biomeManager;
        this.cloudManager = deps.cloudManager || this.cloudManager;
        this.lightingManager = deps.lightingManager || this.lightingManager;
        this.skyManager = deps.skyManager || this.skyManager;
        this.sunManager = deps.sunManager || this.sunManager;
        this.scene = deps.scene || this.scene;
    }

    /**
     * Initialize starting palette from game time
     */
    calculateStartingPaletteFromGameTime() {
        if (!this.gameState) return;

        const gameTimeNorm = this.gameState.getGameTimeNormalized();

        // Phase boundaries must match update() method
        // Dawn: 5-7 AM (0.208-0.292) -> palette 3
        // Day: 7 AM-4 PM (0.292-0.667) -> palette 0
        // Sunset: 4-7 PM (0.667-0.792) -> palette 1
        // Night: 7 PM-5 AM (0.792-1.0, 0.0-0.208) -> palette 2
        if (gameTimeNorm >= 0.208 && gameTimeNorm < 0.292) {
            this.currentPaletteIndex = 3; // Dawn
        } else if (gameTimeNorm >= 0.292 && gameTimeNorm < 0.667) {
            this.currentPaletteIndex = 0; // Day
        } else if (gameTimeNorm >= 0.667 && gameTimeNorm < 0.792) {
            this.currentPaletteIndex = 1; // Sunset
        } else {
            this.currentPaletteIndex = 2; // Night
        }
    }

    /**
     * Apply the starting palette immediately
     */
    applyStartingPalette() {
        if (this.colorPalettes.length === 0) return;

        const paletteIndex = Math.min(this.currentPaletteIndex, this.colorPalettes.length - 1);
        const palette = this.colorPalettes[paletteIndex];

        // Apply global environmental values
        this.skyColorTop = palette.skyColorTop;
        this.skyColorBottom = palette.skyColorBottom;
        this.fogColor = palette.fogColor;

        // Get biome-specific colors
        const biomeColors = this.getBiomeColors(paletteIndex);

        // Pick random terrain color from biome
        if (biomeColors.terrainColors.length > 0) {
            const randomIndex = Math.floor(Math.random() * biomeColors.terrainColors.length);
            this.terrainColor = biomeColors.terrainColors[randomIndex];
            this.currentTerrainColor = this.terrainColor;
        }

        // Pick random cloud color from global palette
        if (palette.cloudColors && palette.cloudColors.length > 0) {
            const randomIndex = Math.floor(Math.random() * palette.cloudColors.length);
            this.currentCloudColor = palette.cloudColors[randomIndex];

            // Force-update all existing clouds to use colors from the new palette
            // This fixes clouds retaining old colors when resuming from simulator
            this._forceUpdateCloudColors(palette.cloudColors);
        }

        // Pick random rock color from biome
        if (biomeColors.rockColors.length > 0) {
            const randomIndex = Math.floor(Math.random() * biomeColors.rockColors.length);
            this.currentRockColor = biomeColors.rockColors[randomIndex];
        }

        // Pick random shrub color from biome
        if (biomeColors.shrubColors.length > 0) {
            const randomIndex = Math.floor(Math.random() * biomeColors.shrubColors.length);
            this.currentShrubColor = biomeColors.shrubColors[randomIndex];
        }

        // Set initial cloud opacity
        if (palette.cloudOpacity !== undefined) {
            this.currentCloudOpacity = palette.cloudOpacity;
            this.interpolatedCloudOpacity = palette.cloudOpacity;
            // Apply to cloud manager if available
            if (this.cloudManager) {
                this.cloudManager.setOpacity(palette.cloudOpacity);
            }
        }

        this.ambientLightColor = palette.ambientLightColor;
        this.ambientLightIntensity = palette.ambientLightIntensity;
        this.directionalLightColor = palette.directionalLightColor;
        this.directionalLightIntensity = palette.directionalLightIntensity;
        this.sunColor = palette.sunColor;
        this.vegetationEmissiveIntensity = palette.vegetationEmissiveIntensity ?? 0.3;

        // Apply colors to scene elements (sky, fog, lighting)
        // Only if dependencies are set (won't be at initial construction)
        this._applyToScene();

        // Update roadside object colors (rocks, shrubs)
        if (this.biomeManager?.currentBiome) {
            this.biomeManager.currentBiome.updateRoadsideObjectColors(
                this.currentRockColor,
                this.currentShrubColor,
                this.vegetationEmissiveIntensity
            );
        }
    }

    /**
     * Get biome-specific colors for a time of day
     */
    getBiomeColors(timeIndex) {
        const biome = this.biomeManager?.currentBiome;
        if (biome && typeof biome.getTerrainColors === 'function') {
            return {
                terrainColors: biome.getTerrainColors(timeIndex) || [],
                rockColors: biome.getRockColors(timeIndex) || [],
                shrubColors: biome.getShrubColors(timeIndex) || []
            };
        }
        // Fallback defaults
        return {
            terrainColors: [0xc4a574, 0xb8a076, 0xd4b896],
            rockColors: [0x8b8378, 0xa09080, 0x9c8c7c],
            shrubColors: [0x6b7e4e, 0x7d7a3d, 0x8a8b5a]
        };
    }

    /**
     * Called when the biome changes to start a gradual color transition.
     * Colors will smoothly interpolate to the new biome's palette over time.
     */
    onBiomeChanged() {
        const biomeColors = this.getBiomeColors(this.currentPaletteIndex);

        console.log(`[ColorCycleManager] onBiomeChanged - starting gradual color transition`);

        // Set next colors from new biome (will be gradually interpolated)
        if (biomeColors.terrainColors.length > 0) {
            this.nextTerrainColor = biomeColors.terrainColors[Math.floor(Math.random() * biomeColors.terrainColors.length)];
        }
        if (biomeColors.rockColors.length > 0) {
            this.nextRockColor = biomeColors.rockColors[Math.floor(Math.random() * biomeColors.rockColors.length)];
        }
        if (biomeColors.shrubColors.length > 0) {
            this.nextShrubColor = biomeColors.shrubColors[Math.floor(Math.random() * biomeColors.shrubColors.length)];
        }

        // Start a biome color transition (similar to sub-cycle but triggered by biome change)
        this.isBiomeColorTransitioning = true;
        this.biomeColorTransitionProgress = 0;
        // Use constant for duration (default: 1800 game seconds = ~30 game minutes)
    }

    /**
     * Update biome color transition (called from main update)
     */
    _updateBiomeColorTransition(deltaTime) {
        if (!this.isBiomeColorTransitioning) return;

        const gameTimeDelta = deltaTime * (this.gameState?.TIME_MULTIPLIER || 1);
        this.biomeColorTransitionProgress += gameTimeDelta / this.biomeColorTransitionDuration;

        if (this.biomeColorTransitionProgress >= 1.0) {
            this.biomeColorTransitionProgress = 1.0;
            this.isBiomeColorTransitioning = false;

            // Commit final colors
            if (this.nextTerrainColor !== null) {
                this.currentTerrainColor = this.nextTerrainColor;
                this.terrainColor = this.nextTerrainColor;
                this.nextTerrainColor = null;
            }
            if (this.nextRockColor !== null) {
                this.currentRockColor = this.nextRockColor;
                this.nextRockColor = null;
            }
            if (this.nextShrubColor !== null) {
                this.currentShrubColor = this.nextShrubColor;
                this.nextShrubColor = null;
            }
            console.log(`[ColorCycleManager] Biome color transition complete`);
        }
    }

    /**
     * Get interpolated terrain color (considers biome transition)
     */
    getInterpolatedTerrainColor() {
        if (this.isBiomeColorTransitioning && this.nextTerrainColor !== null) {
            const t = this.easeInOutCubic(this.biomeColorTransitionProgress);
            return this.lerpColor(this.currentTerrainColor, this.nextTerrainColor, t);
        }
        return this.currentTerrainColor;
    }

    /**
     * Get the current phase name for music
     */
    getPhaseNameForMusic() {
        return getPhaseName(this.currentPaletteIndex);
    }

    /**
     * Helper: Interpolate between two hex colors
     */
    lerpColor(color1, color2, t) {
        const r1 = (color1 >> 16) & 0xff;
        const g1 = (color1 >> 8) & 0xff;
        const b1 = color1 & 0xff;

        const r2 = (color2 >> 16) & 0xff;
        const g2 = (color2 >> 8) & 0xff;
        const b2 = color2 & 0xff;

        const r = Math.round(r1 + (r2 - r1) * t);
        const g = Math.round(g1 + (g2 - g1) * t);
        const b = Math.round(b1 + (b2 - b1) * t);

        return (r << 16) | (g << 8) | b;
    }

    /**
     * Helper: Interpolate between two numbers
     */
    lerp(a, b, t) {
        return a + (b - a) * t;
    }

    /**
     * Helper: Ease-in-out cubic interpolation
     */
    easeInOutCubic(t) {
        return t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    /**
     * Main update method - call each frame
     */
    update(deltaTime) {
        if (!this.enableColorCycle || this.colorPalettes.length < 2) return;
        if (!this.gameState) return;

        // Update biome color transition (gradual color blending between biomes)
        this._updateBiomeColorTransition(deltaTime);

        const gameTimeNorm = this.gameState.getGameTimeNormalized();

        // Phase boundaries (normalized 0.0-1.0)
        const DAWN_START = 0.208;   // 5:00 AM
        const DAWN_END = 0.292;     // 7:00 AM
        const DAY_START = 0.292;    // 7:00 AM
        const DAY_END = 0.667;      // 4:00 PM
        const SUNSET_START = 0.667; // 4:00 PM
        const SUNSET_END = 0.792;   // 7:00 PM
        const NIGHT_START = 0.792;  // 7:00 PM
        const NIGHT_END = 0.208;    // 5:00 AM (next day)

        const TRANSITION_FRACTION = 0.15;
        const SUNSET_TRANSITION_FRACTION = 0.40; // Longer transition into sunset (~1.2 hours)

        let index1;
        let index2;
        let t = 0.0;

        // Determine phase and calculate transition
        if (gameTimeNorm >= DAY_START && gameTimeNorm < DAY_END) {
            const phaseProgress = (gameTimeNorm - DAY_START) / (DAY_END - DAY_START);
            if (phaseProgress < TRANSITION_FRACTION) {
                index1 = 3; // Dawn
                index2 = 0; // Day
                t = phaseProgress / TRANSITION_FRACTION;
            } else {
                index1 = 0;
                index2 = 0;
                t = 0.0;
            }
        }
        else if (gameTimeNorm >= SUNSET_START && gameTimeNorm < SUNSET_END) {
            const phaseProgress = (gameTimeNorm - SUNSET_START) / (SUNSET_END - SUNSET_START);
            if (phaseProgress < SUNSET_TRANSITION_FRACTION) {
                // Gradual transition from day to sunset (4pm-5:12pm)
                index1 = 0; // Day
                index2 = 1; // Sunset
                t = phaseProgress / SUNSET_TRANSITION_FRACTION;
            } else {
                index1 = 1;
                index2 = 1;
                t = 0.0;
            }
        }
        else if (gameTimeNorm >= NIGHT_START || gameTimeNorm < NIGHT_END) {
            let phaseProgress;
            if (gameTimeNorm >= NIGHT_START) {
                phaseProgress = (gameTimeNorm - NIGHT_START) / ((1.0 - NIGHT_START) + NIGHT_END);
            } else {
                phaseProgress = ((1.0 - NIGHT_START) + gameTimeNorm) / ((1.0 - NIGHT_START) + NIGHT_END);
            }
            if (phaseProgress < TRANSITION_FRACTION) {
                index1 = 1; // Sunset
                index2 = 2; // Night
                t = phaseProgress / TRANSITION_FRACTION;
            } else {
                index1 = 2;
                index2 = 2;
                t = 0.0;
            }
        }
        else if (gameTimeNorm >= DAWN_START && gameTimeNorm < DAWN_END) {
            const phaseProgress = (gameTimeNorm - DAWN_START) / (DAWN_END - DAWN_START);
            if (phaseProgress < TRANSITION_FRACTION) {
                index1 = 2; // Night
                index2 = 3; // Dawn
                t = phaseProgress / TRANSITION_FRACTION;
            } else {
                index1 = 3;
                index2 = 3;
                t = 0.0;
            }
        }

        // Handle palette change
        if (index1 !== this.currentPaletteIndex) {
            this._onPaletteChanged(index1);
        }

        // Handle sub-cycle color variation
        this._updateSubCycle(deltaTime, t, index1, index2);

        // Calculate effective transition (considers phase, sub-cycle, and biome transitions)
        let effectiveT = t;
        if (this.isSubCycleTransitioning && t === 0) {
            effectiveT = this.easeInOutCubic(this.subCycleTransitionProgress);
        }
        // Biome transition takes precedence when active
        if (this.isBiomeColorTransitioning) {
            effectiveT = this.easeInOutCubic(this.biomeColorTransitionProgress);
        }

        // Pick next colors if transitioning
        this._pickNextColorsIfNeeded(t, index2);

        // Default to day palette if no phase matched (safety fallback)
        if (index1 === undefined) index1 = 0;
        if (index2 === undefined) index2 = 0;

        // Interpolate colors
        const palette1 = this.colorPalettes[index1];
        const palette2 = this.colorPalettes[index2];

        // Safety check - bail if palettes don't exist
        if (!palette1 || !palette2) {
            return;
        }

        this.skyColorTop = this.lerpColor(palette1.skyColorTop, palette2.skyColorTop, t);
        this.skyColorBottom = this.lerpColor(palette1.skyColorBottom, palette2.skyColorBottom, t);
        this.fogColor = this.lerpColor(palette1.fogColor, palette2.fogColor, t);
        this.ambientLightColor = this.lerpColor(palette1.ambientLightColor, palette2.ambientLightColor, t);
        this.ambientLightIntensity = this.lerp(palette1.ambientLightIntensity, palette2.ambientLightIntensity, t);
        this.directionalLightColor = this.lerpColor(palette1.directionalLightColor, palette2.directionalLightColor, t);
        this.directionalLightIntensity = this.lerp(palette1.directionalLightIntensity, palette2.directionalLightIntensity, t);
        this.sunColor = this.lerpColor(palette1.sunColor, palette2.sunColor, t);
        this.vegetationEmissiveIntensity = this.lerp(
            palette1.vegetationEmissiveIntensity ?? 0.3,
            palette2.vegetationEmissiveIntensity ?? 0.3,
            t
        );

        // Interpolate terrain, rock, shrub colors
        const interpolatedTerrainColor = this._interpolateColor(this.currentTerrainColor, this.nextTerrainColor, effectiveT);
        const interpolatedRockColor = this._interpolateColor(this.currentRockColor, this.nextRockColor, effectiveT);
        const interpolatedShrubColor = this._interpolateColor(this.currentShrubColor, this.nextShrubColor, effectiveT);

        // Interpolate cloud opacity
        if (t > 0 && this.nextCloudOpacity != null && this.currentCloudOpacity != null) {
            this.interpolatedCloudOpacity = this.lerp(this.currentCloudOpacity, this.nextCloudOpacity, t);
        } else if (this.currentCloudOpacity != null) {
            this.interpolatedCloudOpacity = this.currentCloudOpacity;
        } else {
            this.interpolatedCloudOpacity = 0.8;
        }

        // Apply to terrain materials
        if (this.biomeManager?.currentBiome?.terrainMaterials) {
            for (const mat of this.biomeManager.currentBiome.terrainMaterials) {
                mat.color.setHex(interpolatedTerrainColor);
            }
        }

        // Update individual cloud colors
        this._updateCloudColors(t, effectiveT, palette2);

        // Update roadside object colors
        if (this.biomeManager?.currentBiome) {
            this.biomeManager.currentBiome.updateRoadsideObjectColors(interpolatedRockColor, interpolatedShrubColor, this.vegetationEmissiveIntensity);
        }

        // Apply to scene
        this._applyToScene();
    }

    _interpolateColor(current, next, t) {
        if (t > 0 && next !== null) {
            return this.lerpColor(current, next, t);
        }
        return current;
    }

    _onPaletteChanged(newIndex) {
        // Commit transition colors
        if (this.nextTerrainColor !== null) {
            this.currentTerrainColor = this.nextTerrainColor;
            this.terrainColor = this.nextTerrainColor;
        }
        if (this.nextCloudColor !== null) {
            this.currentCloudColor = this.nextCloudColor;
        }
        if (this.nextRockColor !== null) {
            this.currentRockColor = this.nextRockColor;
        }
        if (this.nextShrubColor !== null) {
            this.currentShrubColor = this.nextShrubColor;
        }

        // Update cloud colors
        const clouds = this.cloudManager ? this.cloudManager.getClouds() : [];
        for (const cloud of clouds) {
            if (cloud?.userData?.nextColor !== null) {
                cloud.userData.currentColor = cloud.userData.nextColor;
                cloud.userData.nextColor = null;
            }
        }

        if (this.nextCloudOpacity != null) {
            this.currentCloudOpacity = this.nextCloudOpacity;
        }

        this.currentPaletteIndex = newIndex;
        this.nextTerrainColor = null;
        this.nextCloudColor = null;
        this.nextRockColor = null;
        this.nextShrubColor = null;
        this.nextCloudOpacity = null;

        // Update biome manager
        if (this.biomeManager) {
            this.biomeManager.setPalette(this.colorPalettes[newIndex], newIndex);
        }

    }

    _updateSubCycle(deltaTime, t, index1, index2) {
        const currentGameTime = this.gameState.state.gameTime;
        const timeSinceLastSubCycle = currentGameTime - this.lastSubCycleTime;

        // Start new sub-cycle if enough time passed and not in main transition
        if (timeSinceLastSubCycle >= this.subCycleDuration && t === 0 && index1 === index2) {
            if (!this.isSubCycleTransitioning) {
                this.isSubCycleTransitioning = true;
                this.subCycleTransitionProgress = 0;
                this.lastSubCycleTime = currentGameTime;

                const currentPalette = this.colorPalettes[index1];
                const biomeColors = this.getBiomeColors(index1);

                // Pick new colors
                if (biomeColors.terrainColors.length > 0) {
                    this.nextTerrainColor = biomeColors.terrainColors[Math.floor(Math.random() * biomeColors.terrainColors.length)];
                }
                if (biomeColors.rockColors.length > 0) {
                    this.nextRockColor = biomeColors.rockColors[Math.floor(Math.random() * biomeColors.rockColors.length)];
                }
                if (biomeColors.shrubColors.length > 0) {
                    this.nextShrubColor = biomeColors.shrubColors[Math.floor(Math.random() * biomeColors.shrubColors.length)];
                }

                // Pick new cloud colors
                if (currentPalette.cloudColors?.length > 0) {
                    const clouds = this.cloudManager ? this.cloudManager.getClouds() : [];
                    for (const cloud of clouds) {
                        if (cloud?.userData) {
                            cloud.userData.nextColor = currentPalette.cloudColors[Math.floor(Math.random() * currentPalette.cloudColors.length)];
                        }
                    }
                }
            }
        }

        // Update sub-cycle progress
        if (this.isSubCycleTransitioning) {
            const gameTimeDelta = deltaTime * this.gameState.TIME_MULTIPLIER;
            this.subCycleTransitionProgress += gameTimeDelta / this.subCycleTransitionDuration;

            if (this.subCycleTransitionProgress >= 1.0) {
                this.subCycleTransitionProgress = 0;
                this.isSubCycleTransitioning = false;

                // Only commit sub-cycle colors if we're not in a main phase transition
                // If t > 0, a main transition has started and will handle color changes
                if (t === 0) {
                    // Commit colors
                    if (this.nextTerrainColor !== null) {
                        this.currentTerrainColor = this.nextTerrainColor;
                        this.terrainColor = this.nextTerrainColor;
                        this.nextTerrainColor = null;
                    }
                    if (this.nextRockColor !== null) {
                        this.currentRockColor = this.nextRockColor;
                        this.nextRockColor = null;
                    }
                    if (this.nextShrubColor !== null) {
                        this.currentShrubColor = this.nextShrubColor;
                        this.nextShrubColor = null;
                    }

                    // Commit cloud colors
                    const clouds = this.cloudManager ? this.cloudManager.getClouds() : [];
                    for (const cloud of clouds) {
                        if (cloud?.userData?.nextColor !== null) {
                            cloud.userData.currentColor = cloud.userData.nextColor;
                            cloud.userData.nextColor = null;
                        }
                    }
                }
                // If t > 0, the main transition will pick new next colors via _pickNextColorsIfNeeded
            }
        }
    }

    _pickNextColorsIfNeeded(t, index2) {
        if (t > 0) {
            const biomeColors = this.getBiomeColors(index2);
            const palette2 = this.colorPalettes[index2];

            if (this.nextTerrainColor === null && biomeColors.terrainColors.length > 0) {
                this.nextTerrainColor = biomeColors.terrainColors[Math.floor(Math.random() * biomeColors.terrainColors.length)];
            }
            if (this.nextCloudColor === null && palette2.cloudColors?.length > 0) {
                this.nextCloudColor = palette2.cloudColors[Math.floor(Math.random() * palette2.cloudColors.length)];
            }
            if (this.nextRockColor === null && biomeColors.rockColors.length > 0) {
                this.nextRockColor = biomeColors.rockColors[Math.floor(Math.random() * biomeColors.rockColors.length)];
            }
            if (this.nextShrubColor === null && biomeColors.shrubColors.length > 0) {
                this.nextShrubColor = biomeColors.shrubColors[Math.floor(Math.random() * biomeColors.shrubColors.length)];
            }
            if (this.nextCloudOpacity == null && palette2.cloudOpacity !== undefined) {
                this.nextCloudOpacity = palette2.cloudOpacity;
            }
        }
    }

    _updateCloudColors(t, effectiveT, palette2) {
        const clouds = this.cloudManager ? this.cloudManager.getClouds() : [];
        for (const cloud of clouds) {
            if (!cloud?.material?.color || !cloud.userData) continue;

            // Pick next color if transitioning
            if (t > 0 && cloud.userData.nextColor === null && palette2.cloudColors) {
                cloud.userData.nextColor = palette2.cloudColors[Math.floor(Math.random() * palette2.cloudColors.length)];
            }

            // Interpolate
            let cloudColor;
            if (effectiveT > 0 && cloud.userData.nextColor !== null && cloud.userData.currentColor !== null) {
                cloudColor = this.lerpColor(cloud.userData.currentColor, cloud.userData.nextColor, effectiveT);
            } else if (cloud.userData.currentColor !== null) {
                cloudColor = cloud.userData.currentColor;
            } else {
                continue;
            }

            cloud.material.color.setHex(cloudColor);
        }
    }

    /**
     * Force all existing clouds to use colors from a palette
     * Called when resuming from simulator to fix stale cloud colors
     */
    _forceUpdateCloudColors(cloudColors) {
        const clouds = this.cloudManager ? this.cloudManager.getClouds() : [];
        for (const cloud of clouds) {
            if (!cloud?.material?.color || !cloud.userData) continue;

            // Pick a random color from the new palette
            const newColor = cloudColors[Math.floor(Math.random() * cloudColors.length)];

            // Update both current and material color, clear any pending transition
            cloud.userData.currentColor = newColor;
            cloud.userData.nextColor = null;
            cloud.material.color.setHex(newColor);
        }
    }

    _applyToScene() {
        // Update sky
        if (this.skyManager) {
            this.skyManager.setColors(this.skyColorTop, this.skyColorBottom);
        }

        // Update sun
        if (this.sunManager) {
            this.sunManager.setColor(this.sunColor);
        }

        // Update fog
        if (this.scene?.fog) {
            this.scene.fog.color.setHex(this.fogColor);
        }

        // Update road fog
        if (this.biomeManager?.currentBiome) {
            this.biomeManager.currentBiome.updateRoadFog(this.fogColor);
        }

        // Update lighting
        if (this.lightingManager) {
            this.lightingManager.setAmbient(this.ambientLightColor, this.ambientLightIntensity);
            this.lightingManager.setDirectional(this.directionalLightColor, this.directionalLightIntensity);
        }
    }

    /**
     * Get current palette index
     */
    getCurrentPaletteIndex() {
        return this.currentPaletteIndex;
    }

    /**
     * Set last sub-cycle time (for initialization)
     */
    setLastSubCycleTime(time) {
        this.lastSubCycleTime = time;
    }
}
